import * as binderTypes from "../../enums/binderTypes";
import * as userContentKeys from "../../enums/userContentKeys";
import find from "lodash.find";

const EMPTY_ARR = [];

export function getUserColumns(content, targetName, binderType, fields) {
	const savedLayout =
		content && targetName && binderType && fields
			? getSavedFields(content, binderType, targetName)
			: null;
	return savedLayout
		? savedLayout
				.map(fieldName => find(fields, f => f.name === fieldName))
				.filter(f => !!f)
				.map(f => f.name)
		: EMPTY_ARR;
}

export function getUserColumnOrder(content, targetName, binderType, fields) {
	const savedLayout =
		content && targetName && binderType && fields
			? getSavedFieldOrder(content, targetName, binderType)
			: null;
	return savedLayout
		? savedLayout.map(name => find(fields, f => f.name === name)).filter(f => !!f)
		: EMPTY_ARR;
}

export function getUserColumnWidths(content, targetName, binderType) {
	const savedLayout =
		content && targetName && binderType
			? getSavedFieldWidths(content, targetName, binderType)
			: null;
	return savedLayout ? savedLayout : EMPTY_ARR;
}

function hasDeprecatedLayout(content, binderType, targetName, layoutType) {
	return (
		binderType === binderTypes.BINDER &&
		!!content &&
		!!content[layoutType] &&
		!!content[layoutType][targetName]
	);
}

function hasLayout(content, targetName, binderType, layoutType) {
	return (
		!!content &&
		!!content[userContentKeys.TARGET_LAYOUT] &&
		!!content[userContentKeys.TARGET_LAYOUT][targetName] &&
		!!content[userContentKeys.TARGET_LAYOUT][targetName][binderType] &&
		!!content[userContentKeys.TARGET_LAYOUT][targetName][binderType][layoutType]
	);
}

export function hasSavedLayout(content, targetName, binderType) {
	const layoutType = userContentKeys.DEFAULT_TARGET_FIELDS;
	return (
		hasDeprecatedLayout(content, binderType, targetName, layoutType) ||
		hasLayout(content, targetName, binderType, layoutType)
	);
}

function getSavedBinderTargetLayout(content, targetName, binderType, layoutType) {
	if (hasLayout(content, targetName, binderType, layoutType)) {
		return content[userContentKeys.TARGET_LAYOUT][targetName][binderType][layoutType];
	}

	if (hasDeprecatedLayout(content, binderType, targetName, layoutType)) {
		return content[layoutType][targetName];
	}

	return null;
}

export function getSavedFields(content, binderType, targetName) {
	return getSavedBinderTargetLayout(
		content,
		targetName,
		binderType,
		userContentKeys.DEFAULT_TARGET_FIELDS,
	);
}

export function getSavedFieldOrder(content, targetName, binderType) {
	return getSavedBinderTargetLayout(
		content,
		targetName,
		binderType,
		userContentKeys.DEFAULT_TARGET_COLUMN_ORDER,
	);
}

export function getSavedFieldWidths(content, targetName, binderType) {
	return getSavedBinderTargetLayout(
		content,
		targetName,
		binderType,
		userContentKeys.DEFAULT_TARGET_COLUMN_WIDTHS,
	);
}

export function updateLayout(content, targetName, binderType, layoutValues) {
	const layout =
		content && content[userContentKeys.TARGET_LAYOUT]
			? content[userContentKeys.TARGET_LAYOUT]
			: {};
	const nextLayout = { ...layout };

	if (!layout[targetName]) {
		nextLayout[targetName] = {
			[binderType]: {
				...layoutValues,
			},
		};
	} else if (!layout[targetName][binderType]) {
		nextLayout[targetName] = {
			...layout[targetName],
			[binderType]: {
				...layoutValues,
			},
		};
	} else {
		nextLayout[targetName] = {
			...layout[targetName],
			[binderType]: {
				...layout[targetName][binderType],
				...layoutValues,
			},
		};
	}

	const nextContent = {
		...content,
		[userContentKeys.TARGET_LAYOUT]: nextLayout,
	};

	return resetDeprecatedLayout(nextContent, targetName, binderType);
}

function resetDeprecatedLayout(content, targetName, binderType) {
	if (
		hasDeprecatedLayout(content, binderType, targetName, userContentKeys.DEFAULT_TARGET_FIELDS)
	) {
		const nextContent = {
			...content,
			[userContentKeys.DEFAULT_TARGET_FIELDS]: {
				...content[userContentKeys.DEFAULT_TARGET_FIELDS],
			},
			[userContentKeys.DEFAULT_TARGET_COLUMN_WIDTHS]: {
				...content[userContentKeys.DEFAULT_TARGET_COLUMN_WIDTHS],
			},
			[userContentKeys.DEFAULT_TARGET_COLUMN_ORDER]: {
				...content[userContentKeys.DEFAULT_TARGET_COLUMN_ORDER],
			},
		};
		delete nextContent[userContentKeys.DEFAULT_TARGET_FIELDS][targetName];
		delete nextContent[userContentKeys.DEFAULT_TARGET_COLUMN_WIDTHS][targetName];
		delete nextContent[userContentKeys.DEFAULT_TARGET_COLUMN_ORDER][targetName];

		if (!Object.keys(nextContent[userContentKeys.DEFAULT_TARGET_FIELDS]).length) {
			delete nextContent[userContentKeys.DEFAULT_TARGET_FIELDS];
		}
		if (!Object.keys(nextContent[userContentKeys.DEFAULT_TARGET_COLUMN_WIDTHS]).length) {
			delete nextContent[userContentKeys.DEFAULT_TARGET_COLUMN_WIDTHS];
		}
		if (!Object.keys(nextContent[userContentKeys.DEFAULT_TARGET_COLUMN_ORDER]).length) {
			delete nextContent[userContentKeys.DEFAULT_TARGET_COLUMN_ORDER];
		}
		return nextContent;
	}
	return content;
}

export function resetLayout(content, targetName, binderType) {
	const layoutType = userContentKeys.DEFAULT_TARGET_FIELDS;
	let nextContent = content;

	if (hasDeprecatedLayout(nextContent, binderType, targetName, layoutType)) {
		nextContent = resetDeprecatedLayout(content, targetName, binderType);
	}

	if (hasLayout(nextContent, targetName, binderType, layoutType)) {
		const layout = nextContent[userContentKeys.TARGET_LAYOUT];
		const nextLayout = {
			...layout,
			[targetName]: {
				...layout[targetName],
				[binderType]: {
					...layout[targetName][binderType],
				},
			},
		};
		delete nextLayout[targetName][binderType];
		nextContent = {
			...nextContent,
			[userContentKeys.TARGET_LAYOUT]: nextLayout,
		};
	}

	return nextContent;
}

export function updateLayouts(content, targetName, binderType, fields, widths, order) {
	if (!fields || !widths || !order) {
		return content;
	}

	const layout = {
		[userContentKeys.DEFAULT_TARGET_FIELDS]: fields,
		[userContentKeys.DEFAULT_TARGET_COLUMN_WIDTHS]: widths,
		[userContentKeys.DEFAULT_TARGET_COLUMN_ORDER]: order,
	};
	return updateLayout(content, targetName, binderType, layout);
}

export function hasGlobalDefaults(content, binderType, binderTabs) {
	return (
		!!content &&
		binderTabs &&
		binderTabs.some(tab => {
			const tabTarget = tab.search ? tab.search.target_name : null;
			return !!tabTarget && hasSavedLayout(content, tabTarget, binderType);
		})
	);
}
